/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import Ajv, { ValidateFunction, ErrorObject } from "ajv";

import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { ICommand, TCommandArgs, TGetCommandArgs, TGetCommandRes } from "./ICommand";
import { ErrorTracker } from "@adapptio/lib-components";
import { Breadcrumb } from "../decorators";

interface ICommandRegistry {
	[K: string]: {
		command: ICommand;
		argValidators: ValidateFunction[];
	};
}

/**
 * Command event
 */
export interface ICommandEvent {
	commandId: string;
	args: TCommandArgs;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	res?: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	error?: any;
}

/**
 * Service options
 */
export interface ICommandsServiceOpts {}

/**
 * Service to manage commands
 */
export class CommandsService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** AJV instance */
	private ajv = new Ajv({
		allErrors: true
	});

	/** Registered commands */
	private commandRegistry: ICommandRegistry = {};

	/** Emitted when arguments are valid and command will be executed */
	public onWillExecuteCommand = createEventEmitter<ICommandEvent>();

	/** Emitted when command was executed */
	public onDidExecuteCommand = createEventEmitter<ICommandEvent>();

	/**
	 * Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(private dc: IEditor, opts: ICommandsServiceOpts) {
		this.logger = dc.get("logger").facility("CommandsService");
	}

	/**
	 * Registers command
	 *
	 * @param command Command definition
	 */
	public register(command: ICommand): void {
		if (this.commandRegistry[command.id]) {
			throw new Error(`Command with ID '${command.id}' is already registered.`);
		}

		const argValidators: ValidateFunction[] = [];

		if (command.args) {
			for (let i = 0; i < command.args.length; i++) {
				try {
					argValidators[i] = command.args[i].schema
						? this.ajv.compile(command.args[i].schema)
						: null;
				} catch (err) {
					throw new Error(
						`Failed to register command ID '${command.id}' - schema for argument #${i} is invalid: ` +
							err
					);
				}
			}
		}

		this.commandRegistry[command.id] = {
			command: command,
			argValidators: argValidators
		};
	}

	/**
	 * Returns if a command is registered
	 *
	 * @param commandId Command ID
	 */
	public isRegistered(commandId: string): boolean {
		return this.commandRegistry[commandId] ? true : false;
	}

	/**
	 * Returns command by ID
	 *
	 * @param commandId Command ID
	 */
	public getById<TCommand extends ICommand>(commandId: string): TCommand {
		return this.commandRegistry[commandId] ? (this.commandRegistry[commandId].command as TCommand) : null;
	}

	/**
	 * Executes commands
	 *
	 * @param commandId Command ID
	 * @param args Arguments
	 */
	@Breadcrumb("command.execute")
	public async execute<TCommand extends ICommand>(
		commandId: string,
		...args: TGetCommandArgs<TCommand>
	): Promise<TGetCommandRes<TCommand>> {
		const commandDef = this.commandRegistry[commandId];

		if (!commandDef) {
			throw new Error(`Cannot execute unknown command with ID '${commandId}'.`);
		}

		const validationErrors: ErrorObject[][] = [];
		let isValid = true;

		if (commandDef.command.args) {
			for (let i = 0; i < commandDef.command.args.length; i++) {
				const argDef = commandDef.command.args[i];

				// Skip validation if optional and has no value
				if (argDef.isOptional && (args[i] === undefined || args[i] === null)) {
					continue;
				}

				if (commandDef.argValidators[i]) {
					if (!commandDef.argValidators[i](args[i])) {
						isValid = false;
						validationErrors[i] = commandDef.argValidators[i].errors;
					}
				}
			}
		}

		if (!isValid) {
			this.logger.error(
				`Failed to execute command ID '${commandId}': Argument validation failed`,
				validationErrors,
				args
			);
			throw new Error(`Failed to execute command ID '${commandId}': Invalid arguments.`);
		}

		emitEvent(this.onWillExecuteCommand, {
			commandId: commandId,
			args: args
		});

		try {
			const res = (await commandDef.command.handler(args)) as TGetCommandRes<TCommand>;

			emitEvent(this.onDidExecuteCommand, {
				commandId: commandId,
				args: args,
				res: res
			});

			return res;
		} catch (err) {
			this.logger.warn(`Failed to execute command:`, {
				commandId: commandId,
				args: args,
				error: err
			});

			const errorTracker = ErrorTracker.getInstance();
			errorTracker.captureException(err);

			emitEvent(this.onDidExecuteCommand, {
				commandId: commandId,
				args: args,
				error: err
			});

			throw err;
		}
	}
}
